/**
 * /c/:slug/pipeline — Donor Pipeline (CHAP-246). Table view over alumni records
 * with stage filter chips, per-stage stats, weighted forecast, and team/admin
 * inline edit (stage / tier / pledged) with an auto-qualify suggestion.
 */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { usePipeline, useUpdatePipeline, useBulkTierPipeline } from '../hooks/usePipeline.js';
import { useUserRoles } from '../hooks/useUserRoles.js';
import { formatCurrency } from '../lib/formatters.js';
import type { PipelineStage, CapacityTier } from '../../shared/pipeline.js';

export default function DonorPipeline() {
  const { slug = '' } = useParams();
  const { data, isLoading, error } = usePipeline(slug);
  const { allowedRoles } = useUserRoles();
  const canEdit = allowedRoles.includes('team') || allowedRoles.includes('admin');
  const update = useUpdatePipeline(slug);
  const bulkTier = useBulkTierPipeline(slug);
  const [filter, setFilter] = useState<PipelineStage | 'all'>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkTierValue, setBulkTierValue] = useState<CapacityTier>('unknown');
  const [bulkResult, setBulkResult] = useState<string | null>(null);

  // CHAP-246 — download the CSV export for this chapter's pipeline.
  async function downloadExport() {
    const res = await fetch(`/api/c/${slug}/pipeline/export`, { credentials: 'include' });
    if (!res.ok) return;
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = `pipeline-${slug}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  }

  async function applyBulkTier() {
    const recordIds = [...selected];
    if (recordIds.length === 0) return;
    const r = await bulkTier.mutateAsync({ recordIds, capacityTier: bulkTierValue });
    setBulkResult(
      r.autoQualifiedCount > 0
        ? `Updated ${r.updatedCount} record${r.updatedCount === 1 ? '' : 's'}; auto-qualified ${r.autoQualifiedCount}.`
        : `Updated ${r.updatedCount} record${r.updatedCount === 1 ? '' : 's'}.`,
    );
    setSelected(new Set());
  }

  if (isLoading) return <div className="p-8 text-center text-sm text-neutral">Loading pipeline…</div>;
  if (error) return <div className="p-8 text-center text-sm text-red-700">Pipeline is available to chapter staff. {error.message}</div>;
  if (!data) return null;

  const records = filter === 'all' ? data.records : data.records.filter((r) => r.pipelineStage === filter);
  const stageLabel = (k: string) => data.stages.find((s) => s.key === k)?.label ?? k;
  const tierLabel = (k: string) => data.tiers.find((t) => t.key === k)?.label ?? k;

  async function setStage(id: string, pipelineStage: PipelineStage) {
    await update.mutateAsync({ id, pipelineStage });
  }
  async function setTier(id: string, capacityTier: CapacityTier, currentStage: PipelineStage) {
    const r = await update.mutateAsync({ id, capacityTier });
    if (r.autoQualifySuggested && currentStage === 'discovered') {
      if (window.confirm('Capacity tier set. Move this record to Qualification?')) {
        await update.mutateAsync({ id, pipelineStage: 'qualified' });
      }
    }
  }

  return (
    <div className="space-y-4 min-w-0">
      <header className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 flex items-start justify-between flex-wrap gap-3 min-w-0">
        <div className="min-w-0">
          <h2 className="text-navy text-xl sm:text-2xl font-bold">Donor Pipeline</h2>
          <p className="text-neutral text-sm mt-1">{data.total} alumni records across {data.stages.length} stages.</p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {canEdit && (
            <button
              type="button"
              onClick={downloadExport}
              className="border border-gray-300 text-navy text-sm font-medium px-3 py-2 rounded hover:bg-gray-50"
              title="Export the pipeline as CSV"
            >
              Export CSV
            </button>
          )}
          <div className="flex flex-col items-end">
            <span className="text-[10px] uppercase tracking-wide text-neutral font-bold">Weighted forecast</span>
            <span className="text-positive text-2xl font-bold">{formatCurrency(data.forecastCents)}</span>
          </div>
        </div>
      </header>

      {/* Stage stat chips (filters) */}
      <div className="flex flex-wrap gap-2 min-w-0">
        <Chip active={filter === 'all'} onClick={() => setFilter('all')} label="All" count={data.total} />
        {data.stages.map((s) => (
          <Chip
            key={s.key}
            active={filter === s.key}
            onClick={() => setFilter(s.key)}
            label={s.label}
            count={data.byStage[s.key]?.count ?? 0}
            sub={data.byStage[s.key]?.pledged ? formatCurrency(data.byStage[s.key]!.pledged) : undefined}
          />
        ))}
      </div>

      {/* Touch-point reminder queue (CHAP-246) */}
      <section className="bg-white rounded-xl shadow-sm border border-amber-200 p-4 min-w-0">
        <h3 className="text-navy text-sm font-semibold mb-2">
          Follow-up queue <span className="text-amber-700">({data.followUpQueue.length})</span>
        </h3>
        {data.followUpQueue.length === 0 ? (
          <p className="text-xs text-neutral">All caught up. No records are past their touch cadence.</p>
        ) : (
          <ul className="space-y-1.5">
            {data.followUpQueue.slice(0, 12).map((f) => (
              <li key={f.id} className="flex items-center justify-between gap-2 text-sm min-w-0">
                <span className="truncate"><span className="font-medium text-navy">{f.name || '(no name)'}</span> <span className="text-xs text-neutral">· {stageLabel(f.pipelineStage)}</span></span>
                <span className="text-xs text-amber-700 flex-shrink-0">
                  {f.daysSinceTouch == null ? 'never touched' : `${f.daysSinceTouch}d since touch`}
                  {f.cadenceDays != null && <span className="text-neutral"> (cadence {f.cadenceDays}d)</span>}
                  {f.ownerName && <span className="text-neutral"> · {f.ownerName}</span>}
                </span>
              </li>
            ))}
            {data.followUpQueue.length > 12 && <li className="text-xs text-neutral">+{data.followUpQueue.length - 12} more</li>}
          </ul>
        )}
      </section>

      {/* Bulk capacity-tier assignment (CHAP-246) */}
      {canEdit && selected.size > 0 && (
        <div className="bg-navy/5 border border-navy/20 rounded-xl p-3 flex items-center gap-2 flex-wrap min-w-0">
          <span className="text-xs font-medium text-navy flex-shrink-0">{selected.size} selected</span>
          <select
            value={bulkTierValue}
            onChange={(e) => setBulkTierValue(e.target.value as CapacityTier)}
            className="text-xs border border-gray-300 rounded px-1.5 py-1"
          >
            {data.tiers.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <button
            type="button"
            disabled={bulkTier.isPending}
            onClick={applyBulkTier}
            className="text-xs px-2.5 py-1 rounded bg-navy text-white hover:bg-navy/90 disabled:opacity-50"
          >
            {bulkTier.isPending ? 'Applying…' : 'Apply'}
          </button>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="text-xs px-2.5 py-1 rounded text-neutral hover:bg-gray-100 ml-auto flex-shrink-0"
          >
            Clear
          </button>
        </div>
      )}
      {bulkResult && <p className="text-xs text-green-700">{bulkResult}</p>}

      <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4 min-w-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-neutral">
            <tr className="border-b border-gray-100">
              {canEdit && (
                <th className="pb-2 pr-2 font-medium">
                  <input
                    type="checkbox"
                    aria-label="Select all visible rows"
                    checked={records.length > 0 && records.every((r) => selected.has(r.id))}
                    onChange={(e) => {
                      const next = new Set(selected);
                      for (const r of records) {
                        if (e.target.checked) next.add(r.id);
                        else next.delete(r.id);
                      }
                      setSelected(next);
                    }}
                  />
                </th>
              )}
              <th className="pb-2 pr-3 font-medium">Alumnus</th>
              <th className="pb-2 pr-3 font-medium">Capacity tier</th>
              <th className="pb-2 pr-3 font-medium">Stage</th>
              <th className="pb-2 pr-3 text-right font-medium">Pledged</th>
              <th className="pb-2 pr-3 font-medium">Owner</th>
              <th className="pb-2 font-medium">Address</th>
            </tr>
          </thead>
          <tbody>
            {records.map((r) => (
              <tr key={r.id} className="border-b border-gray-50">
                {canEdit && (
                  <td className="py-1.5 pr-2">
                    <input
                      type="checkbox"
                      aria-label={`Select ${r.name || r.id}`}
                      checked={selected.has(r.id)}
                      onChange={(e) => {
                        const next = new Set(selected);
                        if (e.target.checked) next.add(r.id);
                        else next.delete(r.id);
                        setSelected(next);
                      }}
                    />
                  </td>
                )}
                <td className="py-1.5 pr-3"><span className="font-medium text-navy">{r.name || '(no name)'}</span></td>
                <td className="py-1.5 pr-3">
                  {canEdit ? (
                    <select value={r.capacityTier} onChange={(e) => setTier(r.id, e.target.value as CapacityTier, r.pipelineStage)} className="text-xs border border-gray-300 rounded px-1.5 py-0.5">
                      {data.tiers.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                    </select>
                  ) : <span className="text-xs">{tierLabel(r.capacityTier)}</span>}
                </td>
                <td className="py-1.5 pr-3">
                  {canEdit ? (
                    <select value={r.pipelineStage} onChange={(e) => setStage(r.id, e.target.value as PipelineStage)} className="text-xs border border-gray-300 rounded px-1.5 py-0.5">
                      {data.stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                  ) : <span className="text-xs">{stageLabel(r.pipelineStage)}</span>}
                </td>
                <td className="py-1.5 pr-3 text-right font-mono">{r.pipelinePledgedAmount ? formatCurrency(r.pipelinePledgedAmount) : '-'}</td>
                <td className="py-1.5 pr-3 text-xs text-neutral">{r.ownerName ?? '-'}</td>
                <td className="py-1.5"><AddressHealthBadge health={r.addressHealth} lastGoodAt={r.addressLastGoodAt} /></td>
              </tr>
            ))}
            {records.length === 0 && (
              <tr><td colSpan={canEdit ? 7 : 6} className="py-3 text-center text-neutral text-xs">No records in this stage.</td></tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function Chip({ active, onClick, label, count, sub }: { active: boolean; onClick: () => void; label: string; count: number; sub?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-xs px-3 py-1.5 rounded-lg border transition-colors min-w-0 ${active ? 'bg-navy text-white border-navy' : 'bg-white text-navy border-gray-200 hover:border-navy/40'}`}
    >
      <span className="font-semibold">{label}</span> <span className="opacity-70">{count}</span>
      {sub && <span className={`ml-1 ${active ? 'text-white/70' : 'text-neutral'}`}>· {sub}</span>}
    </button>
  );
}

// CHAP-254 — address health badge (auto/manual outbound delivery tracking).
function AddressHealthBadge({ health, lastGoodAt }: { health: 'valid' | 'bounced_recently' | 'marked_invalid' | 'unknown'; lastGoodAt: string | null }) {
  const meta: Record<string, { label: string; cls: string }> = {
    valid: { label: 'Valid', cls: 'bg-green-100 text-green-800' },
    bounced_recently: { label: 'Bounced', cls: 'bg-amber-100 text-amber-900' },
    marked_invalid: { label: 'Invalid', cls: 'bg-red-100 text-red-800' },
    unknown: { label: 'Unknown', cls: 'bg-gray-100 text-gray-600' },
  };
  const m = meta[health] ?? meta.unknown;
  const good = lastGoodAt ? new Date(lastGoodAt).toLocaleDateString() : null;
  return (
    <span className="inline-flex items-center gap-1.5 min-w-0">
      <span className={`text-[10px] px-1.5 py-0.5 rounded ${m.cls}`}>{m.label}</span>
      {good && <span className="text-[10px] text-neutral">last ok {good}</span>}
    </span>
  );
}
